import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';

@Component({
    selector: 'app-multi-select-dropdown',
    templateUrl: './multi-select-dropdown.component.html',
    styleUrls: ['../../styles.scss', './multi-select-dropdown.component.scss'],
    standalone: false
})
export class MultiSelectDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('select') input: ElementRef | undefined;
  @Input() loading = false;
  @Input() label = '';
  @Input() options: { value: any; label: string; disabled?: boolean }[] = [];
  @Input() enableClearSelection = false;
  @Input() parentFormGroup: FormGroup = new FormGroup({});
  @Input() controlName = '';
  @Input() error = '';
  @Input() placeholder = '';
  @Input() showCount = false;
  @Input() selectionString = 'selected'; // Deprecated: use singularText/pluralText instead
  @Input() singularText = 'item'; // Singular form (e.g., "site", "item")
  @Input() pluralText = 'items'; // Plural form (e.g., "sites", "items")
  @Input() allSelectedText = 'All'; // Text to show when all are selected (e.g., "All Sites")
  @Input() optionsPanelClassName = '';
  @Input() defaultValue: any;
  @Input() enableSelectAll = true;
  @Input() selectAllLabel = 'Select All';
  @Input() enableSearch = true;
  @Output() checkForErrors: EventEmitter<any> = new EventEmitter();
  @Output() selectionChange: EventEmitter<any[]> = new EventEmitter();

  fieldSubscription: Subscription | undefined;
  isRequired = false;
  isDisabled = false;
  searchForm = new FormGroup({
    text: new FormControl(''),
  });
  filteredOptions: { value: any; label: string; disabled?: boolean }[] = [];
  selectAllValue = -1; // Special value for select all
  @ViewChild('select') private select: MatSelect;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(private cdRef: ChangeDetectorRef) {}

  /**
   * String-equality comparison so a hydrated form value of (e.g.) `728`
   * matches an <mat-option [value]="'728'"> when the option pool was loaded
   * from a payload that stringified ids. Without compareWith mat-select uses
   * identity, which silently drops anything where seed-value type differs
   * from option-value type — leaving the chip area empty on edit. Safe to
   * apply globally; option values are always string/number primitives.
   */
  compareWithValue = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a === undefined || a === null || b === undefined || b === null) return false;
    return String(a) === String(b);
  };

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      setTimeout(() => {
        this.parentFormGroup?.controls[this.controlName].markAsUntouched();
      }, 150);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options) {
      this.filteredOptions = [...this.options];
      this.filterOptions();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';

    this.filteredOptions = [...this.options];

    this.fieldSubscription = control?.valueChanges.subscribe(value => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup?.controls[this.controlName]?.touched) {
        this.parentFormGroup?.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }

      // Emit selection change (excluding select all value)
      const selectedValues = Array.isArray(value) ? value.filter(v => v !== this.selectAllValue) : [];
      this.selectionChange.emit(selectedValues);
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }

    this.searchForm.controls.text.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.filterOptions();
    });
  }

  clearSelection($event: Event) {
    $event.stopPropagation();
    this.parentFormGroup.controls[this.controlName].setValue(this.defaultValue || []);
    this.parentFormGroup.markAsDirty();
  }

  filterOptions() {
    const searchText = this.searchForm.controls.text.value?.toLowerCase() || '';
    if (searchText) {
      this.filteredOptions = [...this.options].filter(o => o.label.toLowerCase().includes(searchText));
    } else {
      this.filteredOptions = [...this.options];
    }
    this.cdRef.detectChanges();
  }

  onSelectAllClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    
    // Get current value and available options
    const currentValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
    const allValues = this.options.filter(opt => !opt.disabled).map(opt => opt.value);
    
    // Check if all are currently selected
    const isAllSelected = allValues.length > 0 && allValues.every(val => currentValue.includes(val));
    
    // Toggle selection
    if (isAllSelected) {
      // Deselect all
      this.parentFormGroup.controls[this.controlName].setValue([], { emitEvent: true });
    } else {
      // Select all (only non-disabled options)
      this.parentFormGroup.controls[this.controlName].setValue([...allValues], { emitEvent: true });
    }
    
    // Force remove selectAllValue if it somehow got added
    setTimeout(() => {
      const formValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
      if (Array.isArray(formValue) && formValue.includes(this.selectAllValue)) {
        const filtered = formValue.filter(v => v !== this.selectAllValue);
        this.parentFormGroup.controls[this.controlName].setValue(filtered, { emitEvent: false });
      }
    }, 10);
  }

  onSelectionChange(event: any): void {
    const currentValue = event.value || [];
    
    // Always remove select all value if present and handle selection
    if (currentValue.includes(this.selectAllValue)) {
      const allValues = this.options.filter(opt => !opt.disabled).map(opt => opt.value);
      const currentWithoutSelectAll = currentValue.filter(v => v !== this.selectAllValue);
      const isAllSelected = allValues.length > 0 && allValues.every(val => currentWithoutSelectAll.includes(val));
      
      // Immediately handle the select all logic
      if (!isAllSelected) {
        // Select all options (only non-disabled)
        this.parentFormGroup.controls[this.controlName].setValue([...allValues], { emitEvent: false });
      } else {
        // Deselect all
        this.parentFormGroup.controls[this.controlName].setValue([], { emitEvent: false });
      }
      this.cdRef.detectChanges();
    } else {
      // Normal selection - ensure select all value is not present
      const filteredValue = currentValue.filter(v => v !== this.selectAllValue);
      const currentFormValue = this.parentFormGroup.controls[this.controlName].value || [];
      
      // Only update if values are different
      const currentArray = Array.isArray(currentFormValue) ? currentFormValue : [];
      const currentSorted = [...currentArray].sort();
      const filteredSorted = [...filteredValue].sort();
      
      if (JSON.stringify(filteredSorted) !== JSON.stringify(currentSorted)) {
        this.parentFormGroup.controls[this.controlName].setValue(filteredValue, { emitEvent: false });
        this.parentFormGroup.controls[this.controlName].updateValueAndValidity();
      }
    }
  }

  isSelectAllChecked(): boolean {
    const currentValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
    const allValues = this.options.filter(opt => !opt.disabled).map(opt => opt.value);
    if (allValues.length === 0) {
      return false;
    }
    return allValues.every(val => currentValue.includes(val));
  }

  isSelectAllIndeterminate(): boolean {
    const currentValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
    const allValues = this.options.filter(opt => !opt.disabled).map(opt => opt.value);
    const selectedCount = allValues.filter(val => currentValue.includes(val)).length;
    return selectedCount > 0 && selectedCount < allValues.length;
  }

  getSelectedCount(): number {
    const currentValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
    if (!Array.isArray(currentValue)) {
      return 0;
    }
    return currentValue.filter(v => v !== this.selectAllValue).length;
  }

  getSelectedCountText(): string {
    const currentValue = this.parentFormGroup?.controls[this.controlName]?.value || [];
    const allValues = this.options.filter(opt => !opt.disabled).map(opt => opt.value);
    
    // Check if all options are selected
    const isAllSelected = allValues.length > 0 && allValues.every(val => currentValue.includes(val));
    
    if (isAllSelected) {
      return this.allSelectedText;
    } else {
      const count = this.getSelectedCount();
      if (count === 0) {
        return '';
      }
      
      // Use singular/plural text if provided, otherwise fall back to selectionString
      const text = count === 1 ? this.singularText : this.pluralText;
      return `${count} ${text}`;
    }
  }

  shouldShowClearButton(): boolean {
    if (this.isDisabled) {
      return false;
    }

    if (!this.enableClearSelection) {
      return false;
    }

    const currentValue = this.parentFormGroup?.getRawValue()[this.controlName];
    
    if (this.defaultValue && Array.isArray(this.defaultValue) && this.defaultValue.length > 0) {
      if (!Array.isArray(currentValue)) {
        return false;
      }
      const currentStr = currentValue.join(',');
      const defaultStr = this.defaultValue.join(',');
      return currentStr !== defaultStr;
    }

    return Array.isArray(currentValue) && currentValue.length > 0;
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
